import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { debounceTime } from 'rxjs/operators';
import { distinctUntilChanged } from 'rxjs/operators';

import { MiscUtil } from 'src/shared/utilities/misc.util';
import { ShiftRecord } from 'src/shared/models/shift-record.model';
import { VarianceReason } from 'src/shared/models/variance-reason.model';
import { VarianceReasonRecord } from 'src/shared/models/variance-reason-record.model';
import { MeasureRecord } from 'src/shared/models/measure-record.model';
import { Constants } from 'src/shared/utilities/constants.util';
import { TotalUosVariance } from '../../base-classes/total-uos-variance.component';

@Component({
  selector: 'smt-enter-reason-uos-block',
  templateUrl: './enter-reason-uos-block.component.html',
  styleUrls: ['./enter-reason-uos-block.component.scss']
})
export class EnterReasonBlockComponent extends TotalUosVariance implements OnInit {
    @Input() shiftRecord: ShiftRecord;
    @Input() enterReasonForm: UntypedFormGroup;
    @Input() enterByBlock: boolean;

    private reasons: VarianceReasonRecord[] = [];
    loadingRecords:boolean;
    constructor() { 
        super();
    }

    ngOnInit() {      
        this.calculateJobTitleVariances();

        this.enterReasonForm.addControl(this.getReasonControlName(), new UntypedFormControl());
        this.enterReasonForm.addControl(this.getExplanationControlName(), new UntypedFormControl());
        this.enterReasonForm.addControl(this.getListControlName(), new UntypedFormArray([]));

        if (MiscUtil.checkArray(this.shiftRecord.varianceReasonRecords)) {
            this.shiftRecord.varianceReasonRecords.forEach((reason) => {
                this.addReasonToList(reason.varianceReasonId, reason.explanation);
            });
        } 
        
        this.setMeasureControls();                
    }

    private calculateJobTitleVariances() {
        if (!this.shiftRecord || 
            !this.shiftRecord.costCenter || 
            !MiscUtil.checkArray(this.shiftRecord.staffingPlans) || 
            !MiscUtil.checkArray(this.shiftRecord.costCenter.jobTitles)) {
            return;
        }
        
        let jobTitles = this.shiftRecord.costCenter.jobTitles;
        let fixedJobTitles = this.shiftRecord.costCenter.fixedJobTitles;

        this.shiftRecord.staffingPlans.forEach(staffingPlan => {
            let isFixed = MiscUtil.checkArray(fixedJobTitles) && fixedJobTitles.some(j => j.id == staffingPlan.jobTitleId);
            let jobTitle = jobTitles.find(j => j.id == staffingPlan.jobTitleId);

            if (jobTitle && staffingPlan.amount != staffingPlan.recommended) {
                this.updateJobTitlesWithVariance(
                    isFixed, 
                    jobTitle.abbreviation, 
                    staffingPlan.jobTitleId, 
                    staffingPlan.amount, 
                    staffingPlan.recommended);
            }            
        });
    }

    private get isReasonNotSelected(): boolean {
        return !MiscUtil.isValidPositiveNumber(this.enterReasonForm.value[this.getReasonControlName()]);
    }

    get isVarianceReasonDuplicated(): boolean {
        let uniqueReasons = this.reasons.filter((value, index, self) => { 
            return self.findIndex(s => s.varianceReasonId === value.varianceReasonId) === index;
        });
        return this.reasons.length !== uniqueReasons.length;
    }

    private get reasonOptions(): VarianceReason[] {
        return this.shiftRecord.costCenter.varianceReasons.filter((reason: VarianceReason) => {
            return this.reasons.findIndex(r => r.varianceReasonId === reason.id) === -1;
        });
    }

    private addReason() {        
        let reasonId = Number(this.enterReasonForm.value[this.getReasonControlName()]);
        let reasonExplanation = this.enterReasonForm.value[this.getExplanationControlName()];

        this.addReasonToList(reasonId, reasonExplanation);
    }

    private addReasonToList(reasonId: number, reasonExplanation: string) {
        let newReason = new VarianceReasonRecord();
        let formGroup = new UntypedFormGroup({});

        if (!MiscUtil.isValidPositiveNumber(reasonId)) {
            return;
        }

        if (!reasonExplanation) {
            reasonExplanation = '';
        }

        newReason.varianceReasonId = +reasonId;
        newReason.explanation = reasonExplanation;

        formGroup.addControl(this.getEditingReasonControlName(), new UntypedFormControl(newReason.varianceReasonId));
        formGroup.addControl(this.getEditingExplanationControlName(), new UntypedFormControl(newReason.explanation));

        this.getReasonFormList().push(formGroup);

        this.reasons.push(newReason);

        this.cancelReason();

        this.shiftRecord.varianceReasonRecords = this.reasons;
    }

    private applyReasonEdit(index: number) {
        let editingReasonControlName = this.getEditingReasonControlName();
        let editingExplanationControlName = this.getEditingExplanationControlName();
        let reason = this.getReasonFormList().at(index);

        this.reasons[index].varianceReasonId = +reason.value[editingReasonControlName];
        this.reasons[index].explanation = reason.value[editingExplanationControlName];
        this.reasons[index].editing = false;
        
        const safeEditReasonControlName = encodeURIComponent(editingReasonControlName);
        const safeEditExplantionControlName = encodeURIComponent(editingExplanationControlName);

        //setValue will trigger 'valueChanges' which is necessary for formValidation
        reason.get(safeEditReasonControlName.replace(/%20/g, ' ')).setValue(this.reasons[index].varianceReasonId);
        reason.get(safeEditExplantionControlName.replace(/%20/g, ' ')).setValue(this.reasons[index].explanation);

        this.shiftRecord.varianceReasonRecords = this.reasons;
    }

    private cancelReason() {
        const safeReasonControlName = encodeURIComponent(this.getReasonControlName());
        const safeExplantionControlName = encodeURIComponent(this.getExplanationControlName());

        this.enterReasonForm.get(safeReasonControlName.replace(/%20/g, ' ')).reset();
        this.enterReasonForm.get(safeExplantionControlName.replace(/%20/g, ' ')).reset();
    }

    private cancelReasonEdit(index: number){
        let reason = this.getReasonFormList().at(index);

        const safeEditReasonControlName = encodeURIComponent(this.getEditingReasonControlName());
        const safeEditExplantionControlName = encodeURIComponent(this.getEditingExplanationControlName());

        reason.get(safeEditReasonControlName.replace(/%20/g, ' ')).reset(this.reasons[index].varianceReasonId);
        reason.get(safeEditExplantionControlName.replace(/%20/g, ' ')).reset(this.reasons[index].explanation);

        this.reasons[index].editing = false;
    }

    private deleteReason(index: number) {
        this.reasons.splice(index, 1);
        this.getReasonFormList().removeAt(index);

        this.shiftRecord.varianceReasonRecords = this.reasons;
    }

    private editReason(index: number) {
        this.reasons[index].editing = true;
    }

    private getEditingExplanationControlName(): string {
        return 'editing-explanation-' + this.getShiftBlockControlName();
    }

    private getEditingReasonControlName(): string {
        return 'editing-reason-' + this.getShiftBlockControlName();
    }

    private getExplanationControlName(): string {
        return 'explanation-' + this.getShiftBlockControlName();
    }

    private getListControlName(): string {
        return 'list-' + this.getShiftBlockControlName();
    }

    private getMeasureControlName(measureId: number): string {
        return 'measure-' + measureId + '-' + this.getShiftBlockControlName();
    }

    private getReasonControlName(): string {
        return 'reason-' + this.getShiftBlockControlName();
    }

    private getShiftBlockControlName(): string {
        return 'block-' + this.shiftRecord.shiftBlockId;
    }

    private getReasonFormList(): UntypedFormArray {
        const safeControlName = encodeURIComponent(this.getListControlName());
        return this.enterReasonForm.get(safeControlName.replace(/%20/g, ' ')) as UntypedFormArray;
    }

    private getReasonLabel(reasonId: number): string {
        const reason = this.shiftRecord.costCenter.varianceReasons.find(reason => reason.id == reasonId);

        if (!reason) {
            return '';
        }

        return reason.reason;
    }

    private hasCompleteStaffingPlan() {
        return MiscUtil.checkArray(this.shiftRecord.staffingPlans) && 
            MiscUtil.isValidNonNegativeNumber(this.shiftRecord.staffingPlans.length) &&
            this.shiftRecord.staffingPlans.some(s => MiscUtil.isValidNumber(s.amount) && MiscUtil.isValidNumber(s.projected) && MiscUtil.isValidNumber(s.recommended));
    }

    private hasReasons(): boolean {
        return MiscUtil.checkArray(this.reasons) && MiscUtil.isValidNonNegativeNumber(this.reasons.length);
    }

    private setMeasureControls() {
        this.shiftRecord.costCenter.measures.forEach(measure => {
            let value = null;

            if (MiscUtil.checkArray(this.shiftRecord.measureRecords)) {
                let record = this.shiftRecord.measureRecords.find(r => r.measureId == measure.id);
                value = record ? record.amount : '';
            }
                 
            let measureControlName = this.getMeasureControlName(measure.id);

            this.enterReasonForm.addControl(measureControlName, new UntypedFormControl(value));

            this.enterReasonForm.controls[measureControlName].valueChanges
                // There is a bug in angular that causes valueChanges subscriptions to fire twice for number inputs.
                // distinctUntilChanged is an rxjs operator that can be used as a work around until SMT is upgraded
                // to a version of angular where this bug is corrected.
                .pipe(distinctUntilChanged())
                .pipe(debounceTime(Constants.DebounceTime))
                .subscribe(value => {
                    if (!MiscUtil.isValidNumber(value)) {
                        return;
                    }

                    if (MiscUtil.checkArray(this.shiftRecord.measureRecords) && this.shiftRecord.measureRecords.some(m => m.measureId == measure.id)) {
                        this.shiftRecord.measureRecords.filter(m => m.measureId == measure.id).forEach(measure => {
                            measure.amount = value;
                        });
                    }
                    else {
                        
                        let measureRecord = new MeasureRecord();

                        measureRecord.amount = value;
                        measureRecord.measureId = measure.id;
                        measureRecord.shiftBlockId = this.shiftRecord.shiftBlockId;
                        measureRecord.shiftRecordId = this.shiftRecord.id;
                        measureRecord.workflowTimeFacility = this.shiftRecord.workflowTimeFacility;
                        
                        if (!MiscUtil.checkArray(this.shiftRecord.measureRecords)) {
                            this.shiftRecord.measureRecords = [];
                        }
                        this.shiftRecord.measureRecords.push(measureRecord);
                    }
                });  
        }); 
    }
}
